import React, { useCallback, useEffect, useState } from 'react';
import { Camera, CheckCircle2, Loader2, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { SelfieStep } from '@/components/staffAttendance/SelfieStep';
import { SectionSelect } from '@/components/staffAttendance/SectionSelect';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, errMsg } from '@/services/staffAttendanceApi';

const RefPhotoDialog = ({ staff, onClose, onSaved }) => {
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setConsent(false); }, [staff]);
  const save = async (photo) => {
    if (!consent) { toast.error('Tick the consent box first'); return; }
    setBusy(true);
    try { await staffAttApi.setReference(staff.staff_id, photo); toast.success(`Reference photo saved for ${staff.staff_name}`); onSaved(); }
    catch (e) { toast.error(errMsg(e, 'Could not save the photo')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!staff} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="ref-photo-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Reference photo — {staff?.staff_name}</DialogTitle>
          <DialogDescription>{staff?.staff_id} · {staff?.section}. Face the camera straight, good light, only one person in the shot.</DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ref-consent-label">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="ref-consent-checkbox" />
          <span>I told the staff member this photo is used only to verify their attendance.</span>
        </label>
        {staff && <SelfieStep allowSwitch busy={busy} onSubmit={save} onCancel={onClose} submitLabel="Save reference" testId="ref-photo" />}
      </DialogContent>
    </Dialog>
  );
};

export const ReferencePhotos = ({ sections, search = '' }) => {
  const [section, setSection] = useState('');
  const [missingOnly, setMissingOnly] = useState(true);
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const load = useCallback(() => {
    setData(null);
    staffAttApi.references({ section: section || undefined, missing_only: missingOnly }).then(setData).catch((e) => toast.error(errMsg(e, 'Failed to load staff')));
  }, [section, missingOnly]);
  useEffect(() => { load(); }, [load]);
  const shown = (data?.staff || []).filter((s) => matchStaff(search, s.staff_name, s.staff_id));

  return (
    <div className="space-y-4" data-testid="reference-photos">
      <div className="flex flex-wrap items-center gap-3">
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="ref-section-select" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={missingOnly} onCheckedChange={setMissingOnly} data-testid="ref-missing-only" />Missing reference photo only</label>
        {data && <span className="ml-auto rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700" data-testid="ref-missing-count">{data.missing} missing</span>}
      </div>
      {!data && <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />}
      {data && data.staff.length > 0 && <SearchNote q={search} shown={shown.length} total={data.staff.length} testId="ref-search-note" />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((s) => (
          <Card key={s.staff_id} className="border-0 shadow" data-testid={`ref-row-${s.staff_id}`}>
            <CardContent className="p-4 flex items-center gap-3">
              {s.has_reference ? <CheckCircle2 className="w-8 h-8 text-emerald-500 shrink-0" /> : <UserX className="w-8 h-8 text-gray-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{s.staff_name}</div>
                <div className="text-xs text-gray-500 truncate">{s.staff_id} · {s.section}</div>
                {s.has_reference && <div className="text-[11px] text-gray-400">by {s.captured_by_name}</div>}
              </div>
              <Button size="sm" variant={s.has_reference ? 'outline' : 'default'} className={s.has_reference ? '' : 'bg-amber-500 hover:bg-amber-600'} onClick={() => setTarget(s)} data-testid={`ref-take-${s.staff_id}`}>
                <Camera className="w-4 h-4 mr-1" />{s.has_reference ? 'Retake' : 'Take photo'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      {data && data.staff.length === 0 && <p className="text-center text-gray-400 py-8" data-testid="ref-empty">{missingOnly ? 'Everyone has a reference photo.' : 'No staff in this section.'}</p>}
      <RefPhotoDialog staff={target} onClose={() => setTarget(null)} onSaved={() => { setTarget(null); load(); }} />
    </div>
  );
};

export default ReferencePhotos;
